import React, { useEffect, useState, useContext } from 'react';
import Api from '../../Api';
import * as Sentry from '@sentry/browser';
import { withAuth } from '../../hoc/Auth';
import { Wrapper, Container, Column } from '../../hoc/Layout';
import Pagination from 'react-js-pagination';
import MerchantTerminalSelect from '../../Components/MerchantTerminalSelect';
import MessageContext from '../../MessageContext';

import classes from './styles.module.css';

const STATUS_LABELS = {
    0: 'Создан',
    1: 'Подтверждён',
    2: 'Отменён',
};

function getStatusBadgeClass(status) {
    switch (status) {
        case 0:
            return classes.BadgeCreated;
        case 1:
            return classes.BadgeConfirmed;
        case 2:
            return classes.BadgeCancelled;
        default:
            return classes.Badge;
    }
}

function formatAmount(amountInKopecks) {
    if (amountInKopecks === null || amountInKopecks === undefined) return '0.00';
    return (Number(amountInKopecks) / 100).toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    try {
        const d = new Date(dateStr);
        return d.toLocaleString('ru-RU');
    } catch (e) {
        return dateStr;
    }
}

const Deposit = withAuth(function (props) {
    const token = props.token;
    const onMessage = useContext(MessageContext).onMessage;

    // List state
    const _deposits = useState([]);
    const deposits = _deposits[0];
    const setDeposits = _deposits[1];

    const _count = useState(0);
    const count = _count[0];
    const setCount = _count[1];

    const _loading = useState(false);
    const loading = _loading[0];
    const setLoading = _loading[1];

    const _page = useState(1);
    const page = _page[0];
    const setPage = _page[1];

    const _terminalId = useState('');
    const terminalId = _terminalId[0];
    const setTerminalId = _terminalId[1];

    const _statusFilter = useState('');
    const statusFilter = _statusFilter[0];
    const setStatusFilter = _statusFilter[1];

    // Detail state
    const _selectedDeposit = useState(null);
    const selectedDeposit = _selectedDeposit[0];
    const setSelectedDeposit = _selectedDeposit[1];

    // Create form state
    const _showCreate = useState(false);
    const showCreate = _showCreate[0];
    const setShowCreate = _showCreate[1];

    const _createTerminalId = useState('');
    const createTerminalId = _createTerminalId[0];
    const setCreateTerminalId = _createTerminalId[1];

    const _createMerchantUuid = useState('');
    const createMerchantUuid = _createMerchantUuid[0];
    const setCreateMerchantUuid = _createMerchantUuid[1];

    const _merchantSettingsList = useState([]);
    const merchantSettingsList = _merchantSettingsList[0];
    const setMerchantSettingsList = _merchantSettingsList[1];

    const _createMsId = useState('');
    const createMsId = _createMsId[0];
    const setCreateMsId = _createMsId[1];

    const _createAmount = useState('');
    const createAmount = _createAmount[0];
    const setCreateAmount = _createAmount[1];

    const _createComment = useState('');
    const createComment = _createComment[0];
    const setCreateComment = _createComment[1];

    const _createFile = useState(null);
    const createFile = _createFile[0];
    const setCreateFile = _createFile[1];

    const _creating = useState(false);
    const creating = _creating[0];
    const setCreating = _creating[1];

    // Cancel state
    const _cancelId = useState(null);
    const cancelId = _cancelId[0];
    const setCancelId = _cancelId[1];

    const _cancelReason = useState('');
    const cancelReason = _cancelReason[0];
    const setCancelReason = _cancelReason[1];

    const _cancelling = useState(false);
    const cancelling = _cancelling[0];
    const setCancelling = _cancelling[1];

    const perPage = 20;

    // Load deposits list
    useEffect(
        function () {
            loadDeposits();
        },
        [page, terminalId, statusFilter, token]
    );

    // Load merchant settings when terminal changes in create form
    useEffect(
        function () {
            if (!createTerminalId || !createMerchantUuid) {
                setMerchantSettingsList([]);
                setCreateMsId('');
                return;
            }
            new Api({ token: token })
                .getTerminalBalances(createTerminalId, createMerchantUuid)
                .then(function (r) {
                    const balances = r && r.balances ? r.balances : Array.isArray(r.data) ? r.data : [];
                    setMerchantSettingsList(balances);
                    if (balances.length === 1) {
                        setCreateMsId(String(balances[0].merchantSettingsId));
                    } else {
                        setCreateMsId('');
                    }
                })
                .catch(function (err) {
                    Sentry.captureException(err);
                    setMerchantSettingsList([]);
                    setCreateMsId('');
                });
        },
        [createTerminalId, createMerchantUuid, token]
    );

    function loadDeposits() {
        setLoading(true);
        const filters = {
            page: page,
            limit: perPage,
        };
        if (terminalId) filters.terminal_id = terminalId;
        if (statusFilter !== '') filters.status = statusFilter;

        new Api({ token: token })
            .getDeposits(filters)
            .then(function (r) {
                setDeposits(Array.isArray(r.data) ? r.data : []);
                setCount(typeof r.count === 'number' ? r.count : 0);
                setLoading(false);
            })
            .catch(function (err) {
                Sentry.captureException(err);
                setDeposits([]);
                setCount(0);
                setLoading(false);
                onMessage({ type: 'error', message: 'Ошибка загрузки списка депозитов' });
            });
    }

    function loadDetail(id) {
        new Api({ token: token })
            .getDepositDetail(id)
            .then(function (r) {
                setSelectedDeposit(r.data || r);
            })
            .catch(function (err) {
                Sentry.captureException(err);
                onMessage({ type: 'error', message: 'Ошибка загрузки деталей депозита' });
            });
    }

    function onCreate() {
        if (!createTerminalId || !createMsId || !createAmount || !createComment) {
            onMessage({ type: 'error', message: 'Заполните все обязательные поля' });
            return;
        }

        const amountKopecks = Math.round(Number(createAmount) * 100);
        if (isNaN(amountKopecks) || amountKopecks <= 0) {
            onMessage({ type: 'error', message: 'Сумма должна быть положительным числом' });
            return;
        }

        setCreating(true);
        const api = new Api({ token: token });
        let promise;

        if (createFile) {
            const formData = new FormData();
            formData.append('terminal_id', Number(createTerminalId));
            formData.append('merchant_settings_id', Number(createMsId));
            formData.append('amount', amountKopecks);
            formData.append('comment', createComment);
            formData.append('payment_proof', createFile);
            promise = api.createDepositMultipart(formData);
        } else {
            const data = {
                terminal_id: Number(createTerminalId),
                merchant_settings_id: Number(createMsId),
                amount: amountKopecks,
                comment: createComment,
            };
            promise = api.createDeposit(data);
        }

        promise
            .then(function () {
                setCreating(false);
                setShowCreate(false);
                resetCreateForm();
                setPage(1);
                loadDeposits();
                onMessage({ type: 'success', message: 'Депозит успешно создан' });
            })
            .catch(function (err) {
                Sentry.captureException(err);
                setCreating(false);
                let msg = 'Ошибка при создании депозита';
                if (err && err.response && err.response.data && err.response.data.message) {
                    msg = err.response.data.message;
                }
                onMessage({ type: 'error', message: msg });
            });
    }

    function onCancel() {
        if (!cancelId) return;
        setCancelling(true);
        const currentCancelId = cancelId;
        new Api({ token: token })
            .cancelDeposit(currentCancelId, { reason: cancelReason })
            .then(function () {
                setCancelling(false);
                setCancelId(null);
                setCancelReason('');
                if (selectedDeposit && selectedDeposit.id === currentCancelId) {
                    loadDetail(currentCancelId);
                }
                loadDeposits();
                onMessage({ type: 'success', message: 'Депозит отменён' });
            })
            .catch(function (err) {
                Sentry.captureException(err);
                setCancelling(false);
                onMessage({ type: 'error', message: 'Ошибка при отмене депозита' });
            });
    }

    function resetCreateForm() {
        setCreateTerminalId('');
        setCreateMerchantUuid('');
        setCreateMsId('');
        setCreateAmount('');
        setCreateComment('');
        setCreateFile(null);
        setMerchantSettingsList([]);
    }

    function onRowClick(deposit) {
        loadDetail(deposit.id);
    }

    function onBackToList() {
        setSelectedDeposit(null);
    }

    function openCreateForm() {
        setShowCreate(true);
        resetCreateForm();
    }

    function closeCreateForm() {
        setShowCreate(false);
        resetCreateForm();
    }

    // Render detail view
    function renderDetail() {
        if (!selectedDeposit) return null;
        const d = selectedDeposit;

        return (
            <div className={classes.Card}>
                <div className={classes.Header}>
                    <div>
                        <span className={classes.BackLink} onClick={onBackToList}>
                            &larr; Назад к списку
                        </span>
                        <h5 className={classes.Title} style={{ marginTop: 8 }}>
                            {'Депозит #' + d.id}
                        </h5>
                    </div>
                    {d.status === 1 && (
                        <button
                            className={classes.BtnDanger}
                            onClick={function () {
                                setCancelId(d.id);
                                setCancelReason('');
                            }}
                        >
                            Отменить
                        </button>
                    )}
                </div>

                <div className={classes.Content}>
                    <div className={classes.DetailGrid}>
                        <div>
                            <div className={classes.DetailLabel}>Мерчант</div>
                            <div className={classes.DetailValue}>{d.merchant_name || '\u2014'}</div>
                        </div>
                        <div>
                            <div className={classes.DetailLabel}>Терминал</div>
                            <div className={classes.DetailValue}>
                                {d.terminal_name
                                    ? d.terminal_name + ' (#' + d.terminal_id + ')'
                                    : d.terminal_id || '\u2014'}
                            </div>
                        </div>
                        <div>
                            <div className={classes.DetailLabel}>Статус</div>
                            <div className={classes.DetailValue}>
                                <span className={getStatusBadgeClass(d.status)}>
                                    {STATUS_LABELS[d.status] || 'Неизвестно'}
                                </span>
                            </div>
                        </div>
                        <div>
                            <div className={classes.DetailLabel}>Сумма</div>
                            <div className={classes.DetailValue}>{formatAmount(d.amount)} &#8381;</div>
                        </div>
                        <div>
                            <div className={classes.DetailLabel}>Дата создания</div>
                            <div className={classes.DetailValue}>{formatDate(d.created_at)}</div>
                        </div>
                        <div>
                            <div className={classes.DetailLabel}>Создал</div>
                            <div className={classes.DetailValue}>
                                {(d.created_by && d.created_by.username) || '\u2014'}
                            </div>
                        </div>
                        <div>
                            <div className={classes.DetailLabel}>Настройка мерчанта</div>
                            <div className={classes.DetailValue}>{d.merchant_settings_id || '\u2014'}</div>
                        </div>
                    </div>

                    {d.has_payment_proof && (
                        <div style={{ marginBottom: 12 }}>
                            <button
                                className={classes.BtnOutline}
                                onClick={function () {
                                    new Api({ token: token }).downloadDepositPaymentProof(d.id);
                                }}
                            >
                                Скачать подтверждение ({d.payment_proof_original_name || 'файл'})
                            </button>
                        </div>
                    )}

                    {d.comment && (
                        <div className={classes.DetailCard}>
                            <div className={classes.DetailLabel}>Комментарий</div>
                            <div style={{ marginTop: 4 }}>{d.comment}</div>
                        </div>
                    )}

                    {d.cancelled_reason && (
                        <div className={classes.DetailCard} style={{ background: '#fff3cd', borderColor: '#ffc107' }}>
                            <div className={classes.DetailLabel}>Причина отмены</div>
                            <div style={{ marginTop: 4 }}>{d.cancelled_reason}</div>
                            {d.cancelled_by && (
                                <div style={{ marginTop: 4, fontSize: '.75rem', color: '#666' }}>
                                    {'Отменил: ' + d.cancelled_by.username + ' (' + formatDate(d.cancelled_at) + ')'}
                                </div>
                            )}
                        </div>
                    )}

                    {cancelId === d.id && (
                        <div
                            className={classes.DetailCard}
                            style={{ marginTop: 16, background: '#fff3cd', borderColor: '#ffc107' }}
                        >
                            <h6 style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#856404' }}>
                                Подтверждение отмены
                            </h6>
                            <div className={classes.CancelInput}>
                                <label style={{ display: 'block', fontSize: '.8125rem', marginBottom: 4 }}>
                                    Причина отмены:
                                </label>
                                <textarea
                                    value={cancelReason}
                                    onChange={function (e) {
                                        setCancelReason(e.target.value);
                                    }}
                                    placeholder="Укажите причину отмены..."
                                />
                            </div>
                            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                                <button className={classes.BtnDanger} onClick={onCancel} disabled={cancelling}>
                                    {cancelling ? 'Отмена...' : 'Подтвердить отмену'}
                                </button>
                                <button
                                    className={classes.BtnOutline}
                                    onClick={function () {
                                        setCancelId(null);
                                        setCancelReason('');
                                    }}
                                >
                                    Отмена
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Render create form
    function renderCreateForm() {
        const amountFloat = Number(createAmount) || 0;
        const amountKopecks = Math.round(amountFloat * 100);

        return (
            <div className={classes.Card}>
                <div className={classes.Header}>
                    <h5 className={classes.Title}>Новый депозит</h5>
                    <button className={classes.BtnOutline} onClick={closeCreateForm}>
                        Закрыть
                    </button>
                </div>
                <div className={classes.Content}>
                    <div className={classes.FormRow}>
                        <div className={classes.FormGroup} style={{ flex: 2 }}>
                            <label>Терминал *</label>
                            <MerchantTerminalSelect
                                mode="admin"
                                token={token}
                                stackOnMobile={true}
                                onMerchantSelect={function (uuid) {
                                    setCreateMerchantUuid(uuid);
                                }}
                                onSelect={function (val) {
                                    setCreateTerminalId(val);
                                }}
                            />
                        </div>
                    </div>

                    {merchantSettingsList.length > 1 && (
                        <div className={classes.FormGroup}>
                            <label>Настройка мерчанта *</label>
                            <select
                                value={createMsId}
                                onChange={function (e) {
                                    setCreateMsId(e.target.value);
                                }}
                            >
                                <option value="">Выберите...</option>
                                {merchantSettingsList.map(function (ms) {
                                    return (
                                        <option key={ms.merchantSettingsId} value={ms.merchantSettingsId}>
                                            {(ms.providerName || 'Провайдер не указан') +
                                                ' / ' + (ms.currencyCode || 'валюта не указана') +
                                                ' (ID: ' + ms.merchantSettingsId + ')'}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    )}

                    {merchantSettingsList.length === 1 && (
                        <div style={{ fontSize: '.8125rem', color: '#666', marginBottom: 12 }}>
                            {'Настройка мерчанта: ' +
                                (merchantSettingsList[0].providerName || 'провайдер не указан') +
                                ' / ' + (merchantSettingsList[0].currencyCode || 'валюта не указана') +
                                ' (ID: ' + merchantSettingsList[0].merchantSettingsId + ')'}
                        </div>
                    )}

                    <div className={classes.FormRow}>
                        <div className={classes.FormGroup}>
                            <label>Сумма (руб.) *</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={createAmount}
                                onChange={function (e) {
                                    setCreateAmount(e.target.value);
                                }}
                                placeholder="Например: 10000.00"
                            />
                            {amountKopecks > 0 && (
                                <div style={{ fontSize: '.75rem', color: '#6c757d', marginTop: 4 }}>
                                    {'= ' + amountKopecks + ' копеек'}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={classes.FormGroup}>
                        <label>Комментарий *</label>
                        <textarea
                            rows={3}
                            value={createComment}
                            onChange={function (e) {
                                setCreateComment(e.target.value);
                            }}
                            placeholder="Описание депозита"
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div className={classes.FormGroup}>
                        <label>Файл подтверждения</label>
                        <input
                            type="file"
                            onChange={function (e) {
                                setCreateFile(e.target.files[0] || null);
                            }}
                        />
                    </div>

                    <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                        <button
                            className={classes.BtnPrimary}
                            onClick={onCreate}
                            disabled={creating || !createTerminalId || !createMsId || !createAmount || !createComment}
                        >
                            {creating ? 'Создание...' : 'Создать депозит'}
                        </button>
                        <button className={classes.BtnOutline} onClick={closeCreateForm}>
                            Отмена
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Render list view
    function renderList() {
        return (
            <div className={classes.Card}>
                <div className={classes.Header}>
                    <h5 className={classes.Title}>Депозиты</h5>
                    <button className={classes.Btn} onClick={openCreateForm}>
                        + Создать депозит
                    </button>
                </div>

                <div className={classes.FilterBar}>
                    <div style={{ flex: 1 }}>
                        <MerchantTerminalSelect
                            mode="admin"
                            token={token}
                            stackOnMobile={true}
                            onSelect={function (val) {
                                setTerminalId(val);
                                setPage(1);
                            }}
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={function (e) {
                            setStatusFilter(e.target.value);
                            setPage(1);
                        }}
                    >
                        <option value="">Все статусы</option>
                        <option value="0">Создан</option>
                        <option value="1">Подтверждён</option>
                        <option value="2">Отменён</option>
                    </select>
                </div>

                {loading ? (
                    <div className={classes.Empty}>Загрузка...</div>
                ) : deposits.length === 0 ? (
                    <div className={classes.Empty}>Депозиты не найдены</div>
                ) : (
                    <React.Fragment>
                    <table className={classes.Table}>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Мерчант</th>
                                <th>Терминал</th>
                                <th>Сумма</th>
                                <th>Статус</th>
                                <th>Комментарий</th>
                                <th>Дата</th>
                                <th>Создал</th>
                            </tr>
                        </thead>
                        <tbody>
                            {deposits.map(function (d) {
                                return (
                                    <tr
                                        key={d.id}
                                        onClick={function () {
                                            onRowClick(d);
                                        }}
                                    >
                                        <td>{d.id}</td>
                                        <td>{d.merchant_name || '\u2014'}</td>
                                        <td>
                                            {d.terminal_name
                                                ? d.terminal_name + ' (#' + d.terminal_id + ')'
                                                : d.terminal_id || '\u2014'}
                                        </td>
                                        <td>{formatAmount(d.amount)} &#8381;</td>
                                        <td>
                                            <span className={getStatusBadgeClass(d.status)}>
                                                {STATUS_LABELS[d.status] || '?'}
                                            </span>
                                        </td>
                                        <td
                                            style={{
                                                maxWidth: 200,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {d.comment || '\u2014'}
                                        </td>
                                        <td>{formatDate(d.created_at)}</td>
                                        <td>{(d.created_by && d.created_by.username) || '\u2014'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    <div className={classes.MobileList}>
                        {deposits.map(function (d) {
                            return (
                                <button
                                    key={d.id}
                                    type="button"
                                    className={classes.MobileCard}
                                    onClick={function () {
                                        onRowClick(d);
                                    }}
                                >
                                    <div className={classes.MobileCardRow}>
                                        <div className={classes.MobilePrimary}>#{d.id}</div>
                                        <span className={getStatusBadgeClass(d.status)}>
                                            {STATUS_LABELS[d.status] || '?'}
                                        </span>
                                    </div>
                                    <div className={classes.MobileCardRow}>
                                        <div className={classes.MobileSecondary}>{d.merchant_name || '\u2014'}</div>
                                        <div className={classes.MobileMuted}>{formatDate(d.created_at)}</div>
                                    </div>
                                    <div className={classes.MobileCardRow}>
                                        <div className={classes.MobileTertiary}>
                                            {d.terminal_name
                                                ? d.terminal_name + ' (#' + d.terminal_id + ')'
                                                : d.terminal_id || '\u2014'}
                                        </div>
                                        <div className={classes.MobileAmount}>{formatAmount(d.amount)} &#8381;</div>
                                    </div>
                                    {d.comment ? <div className={classes.MobileComment}>{d.comment}</div> : null}
                                    <div className={classes.MobileCardFooter}>
                                        <span>{(d.created_by && d.created_by.username) || '\u2014'}</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                    </React.Fragment>
                )}

                {perPage < count && (
                    <div className={classes.PaginationWrap}>
                        <Pagination
                            firstPageText={'\u21E4'}
                            prevPageText={'\u21E0'}
                            lastPageText={'\u21E5'}
                            nextPageText={'\u21E2'}
                            activeLinkClass={classes.ActiveLink}
                            activePage={page}
                            itemsCountPerPage={perPage}
                            totalItemsCount={count}
                            pageRangeDisplayed={5}
                            onChange={function (p) {
                                setPage(p);
                            }}
                        />
                    </div>
                )}
            </div>
        );
    }

    // Determine which view to show
    let content;
    if (showCreate) {
        content = renderCreateForm();
    } else if (selectedDeposit) {
        content = renderDetail();
    } else {
        content = renderList();
    }

    return (
        <Wrapper>
            <Container>
                <Column width="full">{content}</Column>
            </Container>
        </Wrapper>
    );
});

export default Deposit;



// WEBPACK FOOTER //
// ./src/PagesOfAdministrator/Deposit/index.js